import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import SectionTabs from '../components/SectionTabs';
import EventList from '../components/EventList';
import EventDetailModal from '../components/EventDetailModal';
import { EVENTS_TABS } from '../lib/section-tabs';
import { useFavorites } from '../lib/favorites';
import { useTasteSettings } from '../lib/tasteSettings';
import { useTasteMatch, matchLevel, type TasteMatch } from '../lib/tasteMatch';
import { confirmDialog } from '../lib/dialogs';
import { api } from '../lib/api';
import { swrLoad, hasFreshSwrCache } from '../lib/swrCache';
import type { ExtractedEvent } from '../types';
import { eventTodayKey } from '../lib/eventDates';
import { addCalendarMonths, eventDateRangeUtc } from '../lib/eventTemporal';
import { DATE_WINDOW_ERROR } from '../lib/eventFilters';
import {
  buildTasteProfile,
  scoreEvent,
  MIN_TASTE_FOR_SCORING,
  MIN_TASTE_SCORE_THRESHOLD,
  TASTE_PRESETS,
  WEIGHT_KEYS,
  WEIGHT_LABELS,
  WEIGHT_DESCRIPTIONS,
  type PresetName,
  type TasteReason,
  type WeightKey,
  type Weights,
} from '../lib/tasteScore';

/**
 * Dedicated "My taste" page. Two inner tabs:
 *   - Recommended: events similar to what the user has marked, sorted by
 *     similarity score, with a "Why?" popover surfacing the top reasons.
 *   - My taste set: the raw set of marked events with per-row Remove + a
 *     bulk Clear button + a JSON export (useful for offline review).
 *
 * Data: reuses `/api/events/published` (the cached public endpoint already
 * fetched by HomePage). No additional API calls — scoring is pure client-side.
 */
type InnerTab = 'recommended' | 'marked' | 'settings';

const TAB_LABELS: Record<InnerTab, string> = {
  recommended: 'Recommended',
  marked: 'My taste set',
  settings: 'Settings',
};

const PRESET_LABELS: Record<PresetName, string> = {
  balanced: 'Balanced',
  familiar: 'Familiar',
  adventurous: 'Adventurous',
  local: 'Local',
};

const PRESET_DESCRIPTIONS: Record<PresetName, string> = {
  balanced:    'Even mix of all signals.',
  familiar:    'Lean into venues, series, and tags you\u2019ve marked before.',
  adventurous: 'Mostly semantic vibe \u2014 surface new places that feel like your taste.',
  local:       'Heavily weight events in neighborhoods you\u2019ve marked.',
};

export default function MyTastePage() {
  const isAuthed = useIsAuthenticated();
  const favorites = useFavorites();
  const tasteSettings = useTasteSettings();
  const [innerTab, setInnerTab] = useState<InnerTab>('recommended');
  const [events, setEvents] = useState<ExtractedEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExtractedEvent | null>(null);
  const tasteMatch = useTasteMatch();

  // Reuse the same 12-month window HomePage uses so the score profile sees
  // the full corpus of upcoming events. Align the bounds to NY calendar days so
  // the SWR cache key is stable across the day — otherwise the millisecond
  // component of `toISOString()` makes every mount a fresh key that never hits
  // the cache, which is what made this page feel slow to load.
  useEffect(() => {
    if (!isAuthed) return;
    const today = eventTodayKey();
    const window = eventDateRangeUtc(addCalendarMonths(today, -1)!, addCalendarMonths(today, 12)!);
    if (!window.ok) {
      setError(DATE_WINDOW_ERROR);
      setEvents([]);
      return;
    }
    const { fromUtc: fromIso, toUtc: toIso } = window.value;
    const qs = `from=${fromIso}&to=${toIso}`;
    const key = `events:${fromIso}|${toIso}|h=0`;
    if (!hasFreshSwrCache(key)) setEvents(null);
    let cancelled = false;
    void swrLoad<ExtractedEvent[]>({
      key,
      fetcher: () => api.get<ExtractedEvent[]>(`/api/events/published?${qs}`),
      onData: (data) => { if (!cancelled) setEvents(data); },
      onError: (e) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to load events';
        setError(msg);
      },
    });
    return () => { cancelled = true; };
  }, [isAuthed]);

  const markedEvents = useMemo<ExtractedEvent[]>(() => {
    if (!events) return [];
    return events.filter(e => favorites.tasteIds.has(e.rowKey));
  }, [events, favorites.tasteIds]);

  // Feed the loaded window into the global taste-match profile so match badges
  // stay consistent here and on pages that don't load the marked set.
  useEffect(() => {
    if (events && events.length > 0) tasteMatch.registerEvents(events);
  }, [events, tasteMatch]);

  const profile = useMemo(() => buildTasteProfile(markedEvents), [markedEvents]);

  // Server-supplied cosine similarity for top tag-scored candidates. Empty when
  // the user has too few embeddings available or the request fails — the
  // tag-based ranking stands alone in that case.
  const [embeddingScores, setEmbeddingScores] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!isAuthed || !events || profile.size < MIN_TASTE_FOR_SCORING) {
      setEmbeddingScores(new Map());
      return;
    }
    // Pick the top ~200 by tag score to send to the server. Bounded so we
    // stay under the endpoint's MAX_CANDIDATES (500) and don't pay to score
    // events the user will never see.
    const candidates = events
      .filter(e => !profile.markedRowKeys.has(e.rowKey))
      .map(e => ({ id: e.rowKey, s: scoreEvent(e, profile, undefined, tasteSettings.weights).score }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 200)
      .map(x => x.id);
    if (candidates.length === 0) {
      setEmbeddingScores(new Map());
      return;
    }
    let cancelled = false;
    api.post<{ scores: Record<string, number>; profileSize: number; scoredCount: number }>(
      '/api/me/taste/embedding-scores',
      { candidateIds: candidates }
    )
      .then(res => {
        if (cancelled) return;
        const m = new Map<string, number>();
        for (const [k, v] of Object.entries(res.scores ?? {})) {
          if (typeof v === 'number') m.set(k, v);
        }
        setEmbeddingScores(m);
      })
      .catch(err => {
        // Non-fatal — fall back to tag-only ranking.
        if (!cancelled) {
          console.warn('embedding-scores fetch failed', err);
          setEmbeddingScores(new Map());
        }
      });
    return () => { cancelled = true; };
  }, [isAuthed, events, profile, tasteSettings.weights]);

  const recommendations = useMemo<{ event: ExtractedEvent; score: number; reasons: TasteReason[] }[]>(() => {
    if (!events || profile.size < MIN_TASTE_FOR_SCORING) return [];
    return events
      .filter(e => !profile.markedRowKeys.has(e.rowKey))
      .filter(e => !favorites.dislikeIds.has(e.rowKey))
      .map(e => ({ event: e, ...scoreEvent(e, profile, embeddingScores.get(e.rowKey), tasteSettings.weights) }))
      .filter(x => x.score >= MIN_TASTE_SCORE_THRESHOLD)
      .sort((a, b) => b.score - a.score)
      .slice(0, 100);
  }, [events, profile, embeddingScores, favorites.dislikeIds, tasteSettings.weights]);

  if (!isAuthed) {
    return (
      <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
        <h1>My taste</h1>
        <p className="text-muted">
          Sign in to mark events as part of your taste profile and get personalized recommendations.
        </p>
      </SectionTabs>
    );
  }

  return (
    <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="mb-0">
          <i className="bi bi-hand-thumbs-up me-2" aria-hidden="true" />
          My taste
        </h1>
        <span className="text-muted small">
          {favorites.tasteIds.size} event{favorites.tasteIds.size === 1 ? '' : 's'} marked
        </span>
      </div>

      <ul className="nav nav-pills mb-3" role="tablist">
        {(Object.keys(TAB_LABELS) as InnerTab[]).map(t => (
          <li className="nav-item" key={t} role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={innerTab === t}
              className={`nav-link${innerTab === t ? ' active' : ''}`}
              onClick={() => setInnerTab(t)}
            >
              {TAB_LABELS[t]}
            </button>
          </li>
        ))}
      </ul>

      {error && <div className="alert alert-warning">{error}</div>}

      {innerTab === 'recommended' && (
        <RecommendedTab
          events={events}
          tasteSize={favorites.tasteIds.size}
          recommendations={recommendations}
          onSelect={setSelected}
        />
      )}

      {innerTab === 'marked' && (
        <MarkedTab
          events={events}
          markedEvents={markedEvents}
          tasteSize={favorites.tasteIds.size}
          onRemove={(id) => favorites.toggle('taste', id)}
          onSelect={setSelected}
        />
      )}

      {innerTab === 'settings' && (
        <SettingsTab
          weights={tasteSettings.weights}
          activePreset={tasteSettings.activePreset}
          ready={tasteSettings.ready}
          isSaving={tasteSettings.isSaving}
          tasteSize={favorites.tasteIds.size}
          onSetWeight={tasteSettings.setWeight}
          onApplyPreset={tasteSettings.applyPreset}
          onToggleSignal={tasteSettings.toggleSignal}
          onReset={tasteSettings.resetToDefault}
        />
      )}

      <EventDetailModal event={selected} onClose={() => setSelected(null)} />
    </SectionTabs>
  );
}

function RecommendedTab({
  events,
  tasteSize,
  recommendations,
  onSelect,
}: {
  events: ExtractedEvent[] | null;
  tasteSize: number;
  recommendations: { event: ExtractedEvent; score: number; reasons: TasteReason[] }[];
  onSelect: (e: ExtractedEvent) => void;
}) {
  // Precompute per-event match objects so the shared EventList renders the same
  // match badge (percentage + reasons tooltip) it shows everywhere else, but
  // using the embedding-blended score computed on this page.
  const matchByRowKey = useMemo(() => {
    const m = new Map<string, TasteMatch>();
    for (const { event, score, reasons } of recommendations) {
      m.set(event.rowKey, {
        score,
        percent: Math.round(score * 100),
        level: matchLevel(score),
        reasons,
      });
    }
    return m;
  }, [recommendations]);
  const recommendedEvents = useMemo(
    () => recommendations.map(r => r.event),
    [recommendations]
  );

  if (events === null) return <p className="text-muted">Loading…</p>;
  if (tasteSize === 0) {
    return (
      <EmptyState
        title="No taste data yet"
        body={
          <>
            Mark events you'd attend (or wish you could) with the{' '}
            <i className="bi bi-hand-thumbs-up" aria-hidden="true" /> thumbs-up icon on
            any event card. After {MIN_TASTE_FOR_SCORING} marks, we'll start surfacing similar events here.
          </>
        }
      />
    );
  }
  if (tasteSize < MIN_TASTE_FOR_SCORING) {
    return (
      <EmptyState
        title="A few more marks needed"
        body={
          <>
            Mark at least {MIN_TASTE_FOR_SCORING} events to enable recommendations. You've marked {tasteSize}{' '}
            so far &mdash; <Link to="/events">browse events</Link> and tap the{' '}
            <i className="bi bi-hand-thumbs-up" aria-hidden="true" /> icon on ones you'd attend.
          </>
        }
      />
    );
  }
  if (recommendations.length === 0) {
    return (
      <EmptyState
        title="No matches in the current window"
        body="We couldn't find upcoming events similar to your taste right now. Check back later, or try marking a wider variety of events."
      />
    );
  }
  return (
    <div>
      <p className="small text-muted">
        Top {recommendations.length} upcoming events similar to your marked taste, strongest match
        first. Hover the match badge on any event to see why we recommended it.
      </p>
      <EventList
        items={recommendedEvents}
        density="compact"
        onSelect={onSelect}
        match={(e) => matchByRowKey.get(e.rowKey) ?? null}
        ariaLabel="Recommended events"
      />
    </div>
  );
}

function MarkedTab({
  events,
  markedEvents,
  tasteSize,
  onRemove,
  onSelect,
}: {
  events: ExtractedEvent[] | null;
  markedEvents: ExtractedEvent[];
  tasteSize: number;
  onRemove: (id: string) => Promise<void>;
  onSelect: (e: ExtractedEvent) => void;
}) {
  if (events === null) return <p className="text-muted">Loading…</p>;
  if (tasteSize === 0) {
    return (
      <EmptyState
        title="No taste data yet"
        body="Mark events with the thumbs-up icon to add them here."
      />
    );
  }
  // Marked-set may include events outside the loaded 12-month window (e.g.
  // past events the user marked months ago). Surface a hint when that gap
  // exists so the count reconciles with what's visible below.
  const missingFromWindow = tasteSize - markedEvents.length;

  const handleClearAll = async () => {
    const confirmed = await confirmDialog({
      title: 'Clear taste set',
      body: `Remove all ${tasteSize} marked events from your taste profile? This won\u2019t delete the events themselves.`,
      confirmLabel: 'Clear all',
      danger: true,
    });
    if (!confirmed) return;
    // Sequential to keep within the per-request rate limit; quick for <1000 entries.
    for (const id of Array.from(favoritesArray(markedEvents, tasteSize))) {
      await onRemove(id);
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify({
      exportedAt: new Date().toISOString(),
      taste: markedEvents.map(e => ({
        rowKey: e.rowKey,
        title: e.title,
        startUtc: e.startUtc,
        venueName: e.venueName,
        tags: e.tags ?? [],
      })),
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-taste-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-3">
        <p className="small text-muted mb-0">
          {markedEvents.length} of {tasteSize} marked events visible
          {missingFromWindow > 0 && ` (${missingFromWindow} outside the upcoming-events window)`}.
        </p>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleExport}>
            <i className="bi bi-download me-1" aria-hidden="true" />
            Export JSON
          </button>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleClearAll}>
            <i className="bi bi-trash me-1" aria-hidden="true" />
            Clear all
          </button>
        </div>
      </div>
      <EventList
        items={markedEvents}
        density="compact"
        onSelect={onSelect}
        ariaLabel="My marked taste events"
      />
    </div>
  );
}

/** Pull only the ids we can confidently remove (those currently in the rendered set);
 *  ids outside the loaded window will stay on the server and the user can prune later. */
function favoritesArray(markedEvents: ExtractedEvent[], _tasteSize: number): string[] {
  return markedEvents.map(e => e.rowKey);
}

function EmptyState({ title, body }: { title: string; body: React.ReactNode }) {
  return (
    <div className="text-center text-muted py-5">
      <h2 className="h5">{title}</h2>
      <p className="mb-0">{body}</p>
    </div>
  );
}

/**
 * Per-user customization of the recommender weights.
 *
 * Three controls, all wired to the same `weights` object (single source of
 * truth):
 *   - Preset chips: set all 7 weights at once.
 *   - Sliders: fine-tune individual signals.
 *   - Toggle switches: quickly turn a signal off (\u2192 0) or restore it.
 *
 * Because every control just calls a setter on `useTasteSettings()`, picking
 * a preset visually moves the sliders, toggling a signal slides it to 0,
 * and dragging a slider may switch the active preset chip to "Custom".
 */
function SettingsTab({
  weights,
  activePreset,
  ready,
  isSaving,
  tasteSize,
  onSetWeight,
  onApplyPreset,
  onToggleSignal,
  onReset,
}: {
  weights: Weights;
  activePreset: PresetName | null;
  ready: boolean;
  isSaving: boolean;
  tasteSize: number;
  onSetWeight: (key: WeightKey, value: number) => void;
  onApplyPreset: (name: PresetName) => void;
  onToggleSignal: (key: WeightKey) => void;
  onReset: () => void;
}) {
  const disabled = tasteSize < MIN_TASTE_FOR_SCORING;
  const disabledHint = disabled
    ? `Mark at least ${MIN_TASTE_FOR_SCORING} events to enable recommendation tuning.`
    : undefined;
  const presetNames = Object.keys(TASTE_PRESETS) as PresetName[];

  return (
    <div>
      <p className="small text-muted">
        Tune how recommendations are scored. Pick a preset to get going, then nudge any signal up or down.
        Changes apply everywhere (the Recommended tab, the Home page, the map) and save to your account.
      </p>

      {disabled && (
        <div className="alert alert-info py-2 px-3 small mb-3" role="status">
          {disabledHint}
        </div>
      )}

      {/* Presets */}
      <fieldset disabled={disabled} className="mb-4">
        <legend className="h6">Preset</legend>
        <div className="d-flex flex-wrap gap-2" role="group" aria-label="Recommendation preset">
          {presetNames.map(name => {
            const isActive = activePreset === name;
            return (
              <button
                key={name}
                type="button"
                className={`btn btn-sm mh-preset-chip ${isActive ? 'btn-primary is-active' : 'btn-outline-primary'}`}
                aria-pressed={isActive}
                onClick={() => onApplyPreset(name)}
                title={PRESET_DESCRIPTIONS[name]}
              >
                {isActive && <i className="bi bi-check2 me-1" aria-hidden="true" />}
                {PRESET_LABELS[name]}
              </button>
            );
          })}
          {activePreset === null && (
            <span className="badge text-bg-secondary align-self-center" aria-label="Custom weights active">
              Custom
            </span>
          )}
        </div>
        <p className="form-text small mt-2 mb-0">
          {activePreset
            ? PRESET_DESCRIPTIONS[activePreset]
            : 'Your sliders don\u2019t match any preset \u2014 pick one above to snap back.'}
        </p>
      </fieldset>

      {/* Per-signal sliders + toggles */}
      <fieldset disabled={disabled} className="mb-4">
        <legend className="h6">Signal weights</legend>
        <p className="form-text small mb-3">
          Each signal contributes up to its weight value (0\u20131) to the final score. Turn one off to
          ignore it entirely.
        </p>
        <div className="vstack gap-3">
          {WEIGHT_KEYS.map(key => {
            const value = weights[key];
            const on = value > 0;
            const sliderId = `taste-weight-${key}`;
            const toggleId = `taste-toggle-${key}`;
            return (
              <div key={key} className="row align-items-center g-2">
                <div className="col-12 col-md-3">
                  <label htmlFor={sliderId} className="form-label mb-0 fw-semibold">
                    {WEIGHT_LABELS[key]}
                  </label>
                  <div className="small text-muted">{WEIGHT_DESCRIPTIONS[key]}</div>
                </div>
                <div className="col-9 col-md-6">
                  <input
                    id={sliderId}
                    type="range"
                    className="form-range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={value}
                    onChange={(e) => onSetWeight(key, parseFloat(e.target.value))}
                    aria-valuemin={0}
                    aria-valuemax={1}
                    aria-valuenow={value}
                    aria-valuetext={`${Math.round(value * 100)} percent`}
                    disabled={!on}
                  />
                </div>
                <div className="col-3 col-md-1 text-end small text-muted" aria-hidden="true">
                  {Math.round(value * 100)}%
                </div>
                <div className="col-12 col-md-2">
                  <div className="form-check form-switch">
                    <input
                      id={toggleId}
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={on}
                      onChange={() => onToggleSignal(key)}
                      aria-label={`${WEIGHT_LABELS[key]}: ${on ? 'enabled' : 'disabled'}`}
                    />
                    <label htmlFor={toggleId} className="form-check-label small">
                      {on ? 'On' : 'Off'}
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </fieldset>

      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <span className="small text-muted" aria-live="polite">
          {!ready ? 'Loading\u2026' : isSaving ? 'Saving\u2026' : 'Saved.'}
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onReset}
          disabled={disabled}
        >
          <i className="bi bi-arrow-counterclockwise me-1" aria-hidden="true" />
          Reset to balanced
        </button>
      </div>
    </div>
  );
}
